'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';

import { TableContent } from './TableContent';

// import { useModalContext } from '@/context/ModalsContext';
// import { ArticleCard } from '@/features/ArticleCard/ArticleCard';
import ClientOnly from '@/helpers/ClientOnly';
import { formatDate } from '@/helpers/formatDate';
// import image from '@/public/article_image.png';
import { devices } from '@/styles/breakpoints';
import { RequestForm } from '../Forms/RequestForm/RequestForm';
import { TopPosts } from './TopPosts';
import { RatingWidget } from './RatingWidget';
import { SimilarArticles } from './SimilarArticles';
import { ConversionBanner } from './ConversionBanner';
import { IBlogDetailed, ITopPost, ISimilarBlog } from '@/types/blog';
import { AuthorBlock } from './AuthorBlock';
import axios from 'axios';

export const Detailed = ({
  data,
  posts,
  similar,
}: {
  data: IBlogDetailed;
  posts: ITopPost[];
  similar: ISimilarBlog[];
}) => {
  const [rating, setRating] = useState({
    average:
      +(
        data?.rating?.reduce(
          (sum: number, r: { value: number; ip: string }) => sum + r.value,
          0
        ) / (data?.rating?.length || 0)
      ).toFixed(2) || 0,
    count: data?.rating?.length || 0,
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleRate = async (value: number) => {
    try {
      const res = await axios.post(`/api/public/blog/rate/${data.id}`, {
        rating: value,
      });
      setSubmitted(true);
      setError(null);
      setRating({
        average: value,
        count: res.data.count,
      });

      setTimeout(() => {
        setRating(res.data);
        setSubmitted(false);
      }, 3000);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 429) {
        setError('You have already rated this post');
      } else {
        setError('Failed to submit rating. Please try again.');
      }

      // Clear error after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available — silently no-op
    }
  };

  return (
    <Wrapper>
      <Content>
        <LeftSide>
          {data?.mainImage?.url && (
            <ImageWrapper>
              <Image
                className="main-image"
                width={343}
                height={200}
                src={data?.mainImage?.url}
                alt={data?.meta_title}
              />
            </ImageWrapper>
          )}
          <Title>{data?.title}</Title>
          <TimeStamp>{formatDate(data?.updated_at)}</TimeStamp>
          {data?.author && <AuthorBlock authorName={data.author} />}
          <ClientOnly>
            {data?.content?.map((item, idx) => (
              <React.Fragment key={idx}>
                <div
                  id={item.title}
                  className="content"
                  dangerouslySetInnerHTML={{ __html: item.description }}
                />
                {idx === 0 && data?.content?.length > 1 && <ConversionBanner />}
              </React.Fragment>
            ))}
          </ClientOnly>

          {!!data?.rating && (
            <RatingShareRow>
              <RatingWidget
                votesCount={rating.count}
                rating={rating.average}
                onClick={handleRate}
                submitted={submitted}
                error={error}
              />
              <ShareBlock>
                <ShareLabel>Share:</ShareLabel>
                <a
                  href="https://www.instagram.com/tytum_hvac/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <ShareIconButton as="span">
                    <svg
                      width="36"
                      height="36"
                      viewBox="0 0 36 36"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="18" cy="18" r="17.5" stroke="#C4C4C4" />
                      <path
                        d="M10.0508 17.8506C10.0508 14.1765 10.0508 12.3394 11.1922 11.198C12.3336 10.0566 14.1706 10.0566 17.8447 10.0566C21.5188 10.0566 23.3559 10.0566 24.4973 11.198C25.6387 12.3394 25.6387 14.1765 25.6387 17.8506C25.6387 21.5247 25.6387 23.3617 24.4973 24.5032C23.3559 25.6446 21.5188 25.6446 17.8447 25.6446C14.1706 25.6446 12.3336 25.6446 11.1922 24.5032C10.0508 23.3617 10.0508 21.5247 10.0508 17.8506Z"
                        stroke="#C4C4C4"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21.5361 17.8501C21.5361 19.8891 19.8832 21.542 17.8442 21.542C15.8053 21.542 14.1523 19.8891 14.1523 17.8501C14.1523 15.8111 15.8053 14.1582 17.8442 14.1582C19.8832 14.1582 21.5361 15.8111 21.5361 17.8501Z"
                        stroke="#C4C4C4"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M22.3629 13.3379H22.3555"
                        stroke="#C4C4C4"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ShareIconButton>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Facebook"
                >
                  <ShareIconButton as="span">
                    <svg
                      width="36"
                      height="36"
                      viewBox="0 0 36 36"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="18" cy="18.0059" r="17.5" stroke="#C4C4C4" />
                      <path
                        d="M19.4851 19.0813H21.5361L22.3566 15.7996H19.4851V14.1588C19.4851 13.3137 19.4851 12.5179 21.1259 12.5179H22.3566V9.76134C22.0891 9.72606 21.0792 9.64648 20.0126 9.64648C17.7852 9.64648 16.2034 11.0059 16.2034 13.5024V15.7996H13.7422V19.0813H16.2034V26.0548H19.4851V19.0813Z"
                        fill="#C4C4C4"
                      />
                    </svg>
                  </ShareIconButton>
                </a>
                <ShareIconButton
                  type="button"
                  onClick={handleCopy}
                  aria-label="Copy link"
                >
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 36 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="18" cy="18" r="17.5" stroke="#C4C4C4" />
                    <path
                      d="M20.5 15.5L15.5 20.5"
                      stroke="#C4C4C4"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M17.5 13L19 11.5C20.933 9.567 24.067 9.567 26 11.5C27.933 13.433 27.933 16.567 26 18.5L24.5 20"
                      stroke="#C4C4C4"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M18.5 23L17 24.5C15.067 26.433 11.933 26.433 10 24.5C8.067 22.567 8.067 19.433 10 17.5L11.5 16"
                      stroke="#C4C4C4"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  {copied && <CopiedLabel>Copied!</CopiedLabel>}
                </ShareIconButton>
              </ShareBlock>
            </RatingShareRow>
          )}
        </LeftSide>
        <RightSide>
          {data?.content && data?.content?.length > 1 && (
            <TableContent data={data?.content} />
          )}

          {!!posts?.length && <TopPosts data={posts} />}

          <RequestForm />
        </RightSide>
      </Content>
      <SimilarArticles items={similar} />
    </Wrapper>
  );
};

const Wrapper = styled.article`
  padding-bottom: 80px;
  width: 100%;
  & img {
    width: 100%;
    height: auto;

    max-width: 600px;
    /* max-width: 375px;
    max-height: 375px; */
  }
  & .main-image {
    border-radius: 10px;
    width: 100%;
    max-width: 100%;
    object-fit: contain;
    height: 400px;
  }

  a {
    color: #216fed;
  }
`;

const ImageWrapper = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h1`
  color: #192d52;
  font-family: var(--font-roboto);
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px;
  margin-bottom: 12px;
  text-align: left;
  @media only screen and ${devices.desktop} {
    font-size: 32px;
    font-style: normal;
    font-weight: 600;
    line-height: 40px;
    margin-bottom: 8px;
  }
`;

const TimeStamp = styled.time`
  display: block;
  color: #4d4d4d;
  font-family: var(--font-roboto);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
`;

const Content = styled.div`
  padding-top: 32px;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  margin-bottom: 20px;

  @media only screen and ${devices.desktop} {
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    align-items: start;
    gap: 20px;
  }
`;

const LeftSide = styled.div`
  width: 100%;
  & .content {
    padding-top: 32px;
    & p {
      color: #262626;
      font-family: var(--font-roboto);
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
      text-align: left;
    }

    text-align: left;

    ul {
      list-style-type: disc;
      padding-left: 40px;
    }

    ol {
      list-style-type: decimal;
    }

    h1 {
      display: block;
      font-size: 2em;
      margin-top: 0.67em;
      margin-bottom: 0.67em;
      margin-left: 0;
      margin-right: 0;
      font-weight: bold;
    }

    h2 {
      display: block;
      font-size: 1.5em;
      margin-top: 0.83em;
      margin-bottom: 0.83em;
      margin-left: 0;
      margin-right: 0;
      font-weight: bold;
    }

    h3 {
      display: block;
      font-size: 1.17em;
      margin-top: 1em;
      margin-bottom: 1em;
      margin-left: 0;
      margin-right: 0;
      font-weight: bold;
    }

    h4 {
      display: block;
      font-size: 1em;
      margin-top: 1.33em;
      margin-bottom: 1.33em;
      margin-left: 0;
      margin-right: 0;
      font-weight: bold;
    }

    h5 {
      display: block;
      font-size: 0.83em;
      margin-top: 1.67em;
      margin-bottom: 1.67em;
      margin-left: 0;
      margin-right: 0;
      font-weight: bold;
    }

    h6 {
      display: block;
      font-size: 0.67em;
      margin-top: 2.33em;
      margin-bottom: 2.33em;
      margin-left: 0;
      margin-right: 0;
      font-weight: bold;
    }
  }
`;
const RightSide = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  @media only screen and ${devices.desktop} {
    width: 422px;
    position: sticky;
    top: 130px;
  }
`;

const RatingShareRow = styled.div`
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media only screen and ${devices.desktop} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const ShareBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ShareLabel = styled.span`
  color: #4d4d4d;
  font-family: var(--font-roboto);
  font-size: 14px;
  font-weight: 500;
  margin-right: 4px;
`;

const ShareIconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  opacity: 1;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.65;
  }
`;

const CopiedLabel = styled.span`
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: #fff;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
`;

/* const ArticlesBlock = styled.div`
  display: flex;
  align-items: start;
  gap: 24px;
  flex-direction: column;
`; */
/*
const ArticlesTitle = styled.h4`
  color: #262626;
  font-family: var(--font-roboto);
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 32px;
  margin: 40px 0 24px;
`;
 */
